import {
  Box,
  Typography,
  TextField,
  Paper,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { useState, useEffect } from "react";
import { getCrew } from "../../services/api/getAll";
import { addCrew, deleteCrew, editCrew } from "../../services/api/adminService";

const languages = ["en", "ar", "es"];

const CrewAdminScreen = () => {
  const [crews, setCrews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editedData, setEditedData] = useState<any>({});
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [newCrewData, setNewCrewData] = useState<any>({
    translations: {},
    imageFile: null,
  });
  const [def, setDef]: any = useState();

  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await getCrew();
        const items = response?.items ?? [];
        const formatted = items.map((item: any) => {
          const translations: any = {};
          item.translations?.forEach((t: any) => {
            translations[t.languageCode] = {
              fullName: t.fullName,
              role: t.role,
              bio: t.bio,
            };
          });
          return {
            id: item.id,
            photoUrl: item.photoUrl,
            translations,
          };
        });
        setCrews(formatted);
      } catch (e) {
        console.error("Error loading crew", e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewCrewData({ translations: {}, imageFile: null });
  };

  const handleDialogChange = (lang: string, field: string, value: string) => {
    setNewCrewData((prev: any) => {
      const updated = { ...prev };
      if (!updated.translations[lang]) updated.translations[lang] = {};
      updated.translations[lang][field] = value;
      return updated;
    });
  };

  const handleAddCrew = async () => {
    const formData = new FormData();
    let transIndex = 0;

    for (const lang of languages) {
      const fullName = newCrewData.translations?.[lang]?.fullName ?? "";
      const role = newCrewData.translations?.[lang]?.role ?? "";
      const bio = newCrewData.translations?.[lang]?.bio ?? "";
      if (fullName || role || bio) {
        formData.append(`translations[${transIndex}].languageCode`, lang);
        formData.append(`translations[${transIndex}].fullName`, fullName);
        formData.append(`translations[${transIndex}].role`, role);
        formData.append(`translations[${transIndex}].bio`, bio);
        transIndex++;
      }
    }

    if (newCrewData.imageFile) {
      formData.append("photo", newCrewData.imageFile);
    }

    try {
      await addCrew(formData);
      handleCloseDialog();
      setLoading(true);
      const response = await getCrew();
      const items = response?.items ?? [];
      const formatted = items.map((item: any) => {
        const translations: any = {};
        item.translations?.forEach((t: any) => {
          translations[t.languageCode] = {
            fullName: t.fullName,
            role: t.role,
            bio: t.bio,
          };
        });
        return {
          id: item.id,
          photoUrl: item.photoUrl,
          translations,
        };
      });
      setCrews(formatted);
    } catch (e) {
      console.error("Error adding crew", e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteCrew(id);
      setCrews((s) => s.filter((x) => x.id !== id));
    } catch (e) {
      console.error("Error deleting", e);
    }
  };

  const openEditDialog = (index: number) => {
    const deepCopy = JSON.parse(JSON.stringify(crews[index]));
    setEditIndex(index);
    setDef(deepCopy);
    setEditedData(deepCopy);
    setImageFile(null);
    setEditDialogOpen(true);
  };

  const handleEditChange = (lang: string, field: string, value: string) => {
    setEditedData((prev: any) => {
      const updated = { ...prev };
      if (!updated.translations[lang]) updated.translations[lang] = {};
      updated.translations[lang][field] = value;
      return updated;
    });
  };

  const handleSaveEdit = async () => {
    if (editIndex === null) return;
    const formData = new FormData();
    let transIndex = 0;

    for (const lang of languages) {
      const fullName = editedData.translations?.[lang]?.fullName ?? "";
      const role = editedData.translations?.[lang]?.role ?? "";
      const bio = editedData.translations?.[lang]?.bio ?? "";
      if (fullName || role || bio) {
        formData.append(`translations[${transIndex}].languageCode`, lang);
        formData.append(`translations[${transIndex}].fullName`, fullName);
        formData.append(`translations[${transIndex}].role`, role);
        formData.append(`translations[${transIndex}].bio`, bio);
        transIndex++;
      }
    }

    formData.append("id", editedData.id.toString());

    if (imageFile) {
      formData.append("photo", imageFile);
    }

    try {
      await editCrew(formData);
      setEditDialogOpen(false);
      const response = await getCrew();
      const items = response?.items ?? [];
      const formatted = items.map((item: any) => {
        const translations: any = {};
        item.translations?.forEach((t: any) => {
          translations[t.languageCode] = {
            fullName: t.fullName,
            role: t.role,
            bio: t.bio,
          };
        });
        return {
          id: item.id,
          photoUrl: item.photoUrl,
          translations,
        };
      });
      setCrews(formatted);
    } catch (e) {
      console.error("Error saving edit", e);
    }
  };

  if (loading) {
    return (
      <Box
        height="80vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 5 }, bgcolor: "#f4f7fa" }}>
      <Box display="flex" justifyContent="space-between" mb={4}>
        <Typography variant="h4" fontWeight="bold">
          Crew
        </Typography>
        <Button variant="contained" onClick={handleOpenDialog}>
          + Add New Member
        </Button>
      </Box>

      <Box display="flex" flexWrap="wrap" gap={3}>
        {crews.map((crew, index) => (
          <Paper
            key={crew.id}
            elevation={6}
            sx={{
              width: {
                xs: "90%",
                sm: "calc(45% - 12px)",
                md: "calc(30.333% - 16px)",
              },
              minHeight: 360,
              p: 2,
              borderRadius: 4,
              bgcolor: "#fff",
              boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              mb={2}
            >
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: "#f5f5f5",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  mb: 2,
                }}
              >
                <img
                  src={crew.photoUrl}
                  alt="crew"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>

              {languages.map((lang) => (
                <Box
                  key={lang}
                  width="100%"
                  mt={1}
                  sx={{
                    borderTop: "1px solid #e2e8f0",
                    pt: 1,
                    px: 1,
                  }}
                >
                  <Typography
                    fontWeight="600"
                    fontSize="13px"
                    color="#0ea5e9"
                    textTransform="uppercase"
                    textAlign="center"
                    mb={0.5}
                  >
                    {lang}
                  </Typography>
                  <Typography
                    fontWeight="bold"
                    fontSize="16px"
                    color="#0f172a"
                    textAlign="center"
                  >
                    {crew.translations?.[lang]?.fullName ?? "No Name"}
                  </Typography>
                  <Typography
                    fontSize="14px"
                    color="#475569"
                    textAlign="center"
                    fontStyle="italic"
                    mb={0.5}
                  >
                    {crew.translations?.[lang]?.role ?? "No Role"}
                  </Typography>
                  <Typography
                    fontSize="13px"
                    color="#334155"
                    textAlign="center"
                    whiteSpace="pre-wrap"
                  >
                    {crew.translations?.[lang]?.bio ?? "No Bio"}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box display="flex" justifyContent="center" gap={2}>
              <IconButton
                onClick={() => openEditDialog(index)}
                sx={{
                  bgcolor: "green",
                  "&:hover": { bgcolor: "primary.main", color: "#fff" },
                  borderRadius: 2,
                }}
              >
                <Edit />
              </IconButton>
              <IconButton
                onClick={() => handleDelete(crew.id)}
                sx={{
                  bgcolor: "error.light",
                  "&:hover": { bgcolor: "error.main", color: "#fff" },
                  borderRadius: 2,
                }}
              >
                <Delete />
              </IconButton>
            </Box>
          </Paper>
        ))}
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Add New Crew Member</DialogTitle>
        <DialogContent dividers>
          {languages.map((lang) => (
            <Box key={lang} mb={3}>
              <TextField
                label={`Full Name (${lang})`}
                fullWidth
                value={newCrewData.translations?.[lang]?.fullName ?? ""}
                onChange={(e) =>
                  handleDialogChange(lang, "fullName", e.target.value)
                }
                sx={{ mb: 2 }}
              />
              <TextField
                label={`Role (${lang})`}
                fullWidth
                value={newCrewData.translations?.[lang]?.role ?? ""}
                onChange={(e) =>
                  handleDialogChange(lang, "role", e.target.value)
                }
                sx={{ mb: 2 }}
              />
              <TextField
                label={`Bio (${lang})`}
                fullWidth
                multiline
                minRows={3}
                value={newCrewData.translations?.[lang]?.bio ?? ""}
                onChange={(e) =>
                  handleDialogChange(lang, "bio", e.target.value)
                }
              />
            </Box>
          ))}
          <Button variant="outlined" component="label">
            Upload Image
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setNewCrewData((prev: any) => ({
                    ...prev,
                    imageFile: e.target.files![0],
                  }));
                }
              }}
            />
          </Button>
          {newCrewData.imageFile && (<>
            <Typography mt={1}>
              Selected: {newCrewData.imageFile.name}
            </Typography>
            <Box mt={2} display="flex" justifyContent="center">
              <img
                src={URL.createObjectURL(newCrewData.imageFile)}
                alt="Preview"
                style={{
                  maxWidth: "150px",
                  maxHeight: "150px",
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
              />
            </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleAddCrew} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          if (editIndex !== null) {
            setEditedData(def);
          }
          setImageFile(null);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Edit Crew Member</DialogTitle>
        <DialogContent dividers>
          {languages.map((lang) => (
            <Box key={lang} mb={3}>
              <TextField
                label={`Full Name (${lang})`}
                fullWidth
                value={editedData.translations?.[lang]?.fullName ?? ""}
                onChange={(e) =>
                  handleEditChange(lang, "fullName", e.target.value)
                }
                sx={{ mb: 2 }}
              />
              <TextField
                label={`Role (${lang})`}
                fullWidth
                value={editedData.translations?.[lang]?.role ?? ""}
                onChange={(e) => handleEditChange(lang, "role", e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label={`Bio (${lang})`}
                fullWidth
                multiline
                minRows={3}
                value={editedData.translations?.[lang]?.bio ?? ""}
                onChange={(e) => handleEditChange(lang, "bio", e.target.value)}
              />
            </Box>
          ))}
          {(imageFile || editedData.photoUrl) && (
            <Box mt={2} display="flex" justifyContent="center">
              <img
                src={
                  imageFile
                    ? URL.createObjectURL(imageFile)
                    : editedData.photoUrl
                }
                alt="Preview"
                style={{
                  maxWidth: "150px",
                  maxHeight: "150px",
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
              />
            </Box>
          )}

          <Button variant="outlined" component="label">
            Upload Image
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setImageFile(e.target.files![0]);
                }
              }}
            />
          </Button>
          {imageFile && (
            <>
              <Typography mt={1}>Selected: {imageFile.name}</Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              if (editIndex !== null) {
                setEditedData({ ...crews[editIndex] });
              }
              setImageFile(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrewAdminScreen;
